<script setup lang="ts">
import { ref, computed, watch } from 'vue';

const props = defineProps<{ modelValue: string }>();
const emit = defineEmits<{ (e: 'update:modelValue', v: string): void }>();

const countries = [
  { code: 'BE', label: 'Belgique', flag: '🇧🇪' },
  { code: 'FR', label: 'France', flag: '🇫🇷' },
  { code: 'LU', label: 'Luxembourg', flag: '🇱🇺' },
  { code: 'NL', label: 'Pays-Bas', flag: '🇳🇱' },
  { code: 'DE', label: 'Allemagne', flag: '🇩🇪' },
  { code: 'GB', label: 'Royaume-Uni', flag: '🇬🇧' },
  { code: 'CH', label: 'Suisse', flag: '🇨🇭' },
  { code: 'ES', label: 'Espagne', flag: '🇪🇸' },
  { code: 'IT', label: 'Italie', flag: '🇮🇹' },
  { code: 'PT', label: 'Portugal', flag: '🇵🇹' },
];

const open = ref(false);
const search = ref('');
const customMode = ref(false);
const customValue = ref('');

// Si la valeur actuelle n'est pas dans la liste → c'est un custom
watch(
  () => props.modelValue,
  (val) => {
    if (val && !countries.find((c) => c.code === val)) {
      customMode.value = true;
      customValue.value = val;
    }
  },
  { immediate: true },
);

const selected = computed(
  () => countries.find((c) => c.code === props.modelValue) ?? null,
);

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return countries;
  return countries.filter((c) => c.label.toLowerCase().includes(q));
});

const triggerLabel = computed(() => {
  if (customMode.value && props.modelValue) return props.modelValue;
  return selected.value?.label ?? null;
});

const triggerFlag = computed(() => {
  if (customMode.value) return '🌍';
  return selected.value?.flag ?? null;
});

const openDropdown = () => {
  open.value = true;
  search.value = '';
  customMode.value = false;
};

const pick = (code: string) => {
  emit('update:modelValue', code);
  open.value = false;
  search.value = '';
  customMode.value = false;
};

const clear = () => {
  emit('update:modelValue', '');
  open.value = false;
  customMode.value = false;
  customValue.value = '';
};

const enterCustomMode = () => {
  customMode.value = true;
  open.value = false;
  search.value = '';
  customValue.value = '';
  emit('update:modelValue', '');
};

const confirmCustom = () => {
  const val = customValue.value.trim();
  if (val) emit('update:modelValue', val);
};
</script>

<template>
  <div class="relative">
    <!-- Trigger -->
    <button
      v-if="!customMode"
      type="button"
      @click="openDropdown"
      class="flex h-11 w-full items-center justify-between rounded-xl border border-solid bg-white px-3 transition-colors"
      :style="
        open
          ? 'border-color: #FBAF01; box-shadow: 0 0 0 3px rgba(251,175,1,0.12)'
          : 'border-color: #eeedec'
      "
    >
      <span class="flex items-center gap-2">
        <!-- pays sélectionné = drapeau emoji, mode custom = globe SVG -->
        <span v-if="triggerFlag && !customMode" class="text-lg leading-none">{{
          triggerFlag
        }}</span>
        <svg
          v-else-if="customMode"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          class="shrink-0 text-[#74706B]"
        >
          <circle
            cx="9"
            cy="9"
            r="7"
            stroke="currentColor"
            stroke-width="1.4"
          />
          <path
            d="M9 2C9 2 6.5 5 6.5 9C6.5 13 9 16 9 16"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
          />
          <path
            d="M9 2C9 2 11.5 5 11.5 9C11.5 13 9 16 9 16"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
          />
          <path
            d="M2 9H16"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
          />
          <path
            d="M2.5 6H15.5M2.5 12H15.5"
            stroke="currentColor"
            stroke-width="1.2"
            stroke-linecap="round"
          />
        </svg>
        <span
          class="text-14px font-tt-medium-Normal"
          :class="triggerLabel ? 'text-grand-text' : 'text-[#9CA3AF]'"
        >
          {{ triggerLabel ?? 'Sélectionner un pays' }}
        </span>
      </span>
      <svg
        :class="[
          'text-[#74706B] transition-transform duration-200',
          open ? 'rotate-180' : '',
        ]"
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
      >
        <path
          d="M3 5L7 9L11 5"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <!-- Input custom libre -->
    <div
      v-else
      class="flex h-11 w-full items-center gap-2 rounded-xl border border-solid bg-white px-3"
      style="border-color: #333; box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.12)"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        class="shrink-0 text-[#74706B]"
      >
        <circle cx="9" cy="9" r="7" stroke="currentColor" stroke-width="1.4" />
        <path
          d="M9 2C9 2 6.5 5 6.5 9C6.5 13 9 16 9 16"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />
        <path
          d="M9 2C9 2 11.5 5 11.5 9C11.5 13 9 16 9 16"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />
        <path
          d="M2 9H16"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />
        <path
          d="M2.5 6H15.5M2.5 12H15.5"
          stroke="currentColor"
          stroke-width="1.2"
          stroke-linecap="round"
        />
      </svg>
      <input
        v-model="customValue"
        @input="confirmCustom"
        type="text"
        placeholder="Entrez le nom du pays..."
        class="text-grand-text text-14px font-tt-medium-Normal flex-1 bg-transparent focus:outline-none"
        autofocus
      />
      <button
        type="button"
        @click="openDropdown"
        class="text-[#9CA3AF] transition-colors hover:text-[#74706B]"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M3 5L7 9L11 5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <!-- Dropdown -->
    <div
      v-if="open"
      class="absolute top-[calc(100%+6px)] right-0 left-0 z-50 overflow-hidden rounded-xl border border-solid bg-white"
      style="border-color: #eeedec; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08)"
    >
      <!-- Recherche -->
      <div class="flex items-center gap-2 border-b border-[#EEEDEC] px-3 py-2">
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          class="shrink-0 text-[#9CA3AF]"
        >
          <circle
            cx="7"
            cy="7"
            r="5"
            stroke="currentColor"
            stroke-width="1.5"
          />
          <path
            d="M11 11L14 14"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
        <input
          v-model="search"
          type="text"
          placeholder="Rechercher..."
          class="text-grand-text text-13px font-tt-normal-Twice flex-1 bg-transparent focus:outline-none"
        />
        <button
          v-if="search"
          type="button"
          @click="search = ''"
          class="text-[#9CA3AF] hover:text-[#74706B]"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 2L10 10M10 2L2 10"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>

      <div class="max-h-55 overflow-y-auto">
        <!-- Aucun -->
        <button
          type="button"
          @click="clear"
          class="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-[#F7F7F7]"
        >
          <span class="w-5 text-center text-base">—</span>
          <span class="text-12px font-tt-normal-Twice text-[#9CA3AF]"
            >Aucun</span
          >
        </button>
        <div class="mx-3 h-px bg-[#EEEDEC]" />

        <!-- Liste filtrée -->
        <template v-if="filtered.length > 0">
          <button
            v-for="c in filtered"
            :key="c.code"
            type="button"
            @click="pick(c.code)"
            class="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[#FFF9EE]"
            :class="modelValue === c.code ? 'bg-[#FFF9EE]' : ''"
          >
            <span class="text-lg leading-none">{{ c.flag }}</span>
            <span
              class="text-14px font-tt-medium-Normal flex-1"
              :class="
                modelValue === c.code ? 'text-[#F89303]' : 'text-grand-text'
              "
              >{{ c.label }}</span
            >
            <svg
              v-if="modelValue === c.code"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M2.5 7L5.5 10L11.5 4"
                stroke="#F89303"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </template>
        <p
          v-else
          class="text-12px font-tt-normal-Twice px-3 py-3 text-[#9CA3AF]"
        >
          Aucun résultat
        </p>

        <!-- Séparateur + Autre -->
        <div class="mx-3 h-px bg-[#EEEDEC]" />
        <button
          type="button"
          @click="enterCustomMode"
          class="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[#F7F7F7]"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            class="shrink-0 text-[#74706B]"
          >
            <path
              d="M10.5 3.5L14.5 7.5M2.5 15.5H6.5L14.5 7.5L10.5 3.5L2.5 11.5V15.5Z"
              stroke="currentColor"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="text-14px font-tt-medium-Normal text-[#74706B]"
            >Autre pays...</span
          >
        </button>
      </div>
    </div>
  </div>
</template>
